import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {  Button , SkeletonLoader } from 'app/components';
import {  Plus } from '@phosphor-icons/react';
import {  LiveEvent,  UpcomingEvent,  DraftEvent,  PastEvent, QRScanCard} from './components'
import { DeleteModal, HorizontalTab } from '../components';
import { tabs } from '../data/data';
import { deleteEvent, getEventList } from '../api';
import { formatDistanceToNowStrict } from 'date-fns';
import './style';

const Events = () => {
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false);
  const [eventToDelete, setEventToDelete ] = useState(null);
  const [placeholderMsgs, setPlaceholderMsgs] = useState({
    live: {
      title: "You haven’t created any events yet!",
      content: "Let’s create your first event.",
      cta: 'List your event',
      ctaIcon: (<Plus size={20} color='white' />),
    },
    upcoming: {
      title: "You have no upcoming events!",
      content: "Let’s create your first event.",
      cta: 'List your event',
      ctaIcon: (<Plus size={20} color='white' />),
    },
    draft: {
      title: "You have no events in draft!",
      content: "Let’s create your first event.",
      cta: 'List your event',
      ctaIcon: (<Plus size={20} color='white' />),
    },
    past: {
      title: "You have no past events yet!",
      content: "Let’s create your first event.",
      cta: 'List your event',
      ctaIcon: (<Plus size={20} color='white' />),
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { eventList, eventsCount } = useSelector((state) => state.eventList);
  const { userData, activeRequests } = useSelector((state) => state.app);
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeRequests?.includes('get-event-list')) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [activeRequests]);

  useEffect(() => {
    if (eventsCount?.live?.length) {
      setSelectedTab('Live');
      setPlaceholderMsgs((prev) => ({
        upcoming: { ...prev.upcoming, content: 'Let’s create your event.' },
        draft: { ...prev.draft, content: 'Let’s create your event.' },
        past: { ...prev.past, content: 'Let’s create your event.' },
      }));
    } else if (eventsCount?.upcoming?.length) {
      setSelectedTab('Under Review');
      setPlaceholderMsgs((prev) => ({
        ...prev,
        live: {
          title: "No live events",
          content: "Events will be made live after review.",
          cta: null,
          ctaIcon: null,
        },
      }));
    } else {
      setSelectedTab('Draft');
      if (eventsCount?.past?.length) {
        setPlaceholderMsgs((prev) => ({
          ...prev,
          live: {
            title: `${formatDistanceToNowStrict(eventsCount.past[0]?.start_date)} have passed since you hosted your last event.`,
            content: "Ready to schedule your next event?",
            cta: 'List your event',
            ctaIcon: (<Plus size={20} color='white' />),
          },
        }));
      }
    }
  }, [eventsCount]);

  useEffect(() => {
    if (selectedTab && userData?.business_config_id) {
      getEventList({
        searchText: selectedTab === 'Under Review' ? 'upcoming' : selectedTab.toLowerCase(),
        businessConfigId: userData?.business_config_id,
      });
    } else if (selectedTab === 'Draft') {
      getEventList({
        searchText: selectedTab.toLowerCase(),
        businessConfigId: '-',
      });
    }
  }, [selectedTab, userData?.business_config_id]);

  const handleEdit = (eventId) => {
    navigate(`/event/edit/${eventId}`);
  };
  
  const handleView = (eventId) => {
    navigate(`/event/dashboard/${eventId}`);
  };
  const handleDelete = (eventId, isDraft) => {
    setEventToDelete({ eventId, isDraft });
    setIsDeleteModalOpen(true);
  };
  const handleConfirmDelete = () => {
    deleteEvent(eventToDelete?.eventId, eventToDelete?.isDraft, () => {
      getEventList({
        searchText: selectedTab === 'Under Review' ? 'upcoming' : selectedTab.toLowerCase(),
        businessConfigId: userData?.business_config_id || '-',
      });
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
    });
  };
  
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };

  const renderContent = () => {
    switch(selectedTab) {
    case 'Live':
      return (
        <LiveEvent events={eventList} 
          handleEdit={handleView}
          handleView={handleView}
          placeholderProps={placeholderMsgs.live}
        />
      );
    case 'Under Review':
      return (
        <UpcomingEvent events={eventList} 
          handleEdit={handleView}
          handleView={handleView}
          handleDelete={handleDelete}
          placeholderProps={placeholderMsgs.upcoming}
        />
      );
    case 'Draft':
      return (
        <DraftEvent events={eventList} 
          handleEdit={handleEdit}
          handleDelete={(id) => handleDelete(id, true)}
          placeholderProps={placeholderMsgs.draft}
        />
      );
    case 'Past':
      return(
        <PastEvent events={eventList} placeholderProps={placeholderMsgs.past} handleView={handleView} />
      );
    default:
      return null;
    }
  };

    const onChangeTab = (tab) => {
      if (tab !== selectedTab) {
        setSelectedTab(tab);
        dispatch({ type: 'EVENT_LIST:DATA:SET', data: [] });
      }
    };


  return (
    <>
    <QRScanCard className = "qr-scan-card"/>
    <br />
    <div className="events-container w-full flex flex-col items-start gap-6">
      <div className='heading-24 flex justify-between w-full items-center'>
        Your events
        <Button
          className="btn-create-event"
          iconLeft={<Plus size={20} 
            color={'white'} />}
          onClick={() => navigate('/event/create', { state: { from: window.location.pathname } })}
          children="List your event"
        />
      </div>
      <HorizontalTab
        tabs={tabs}
        onSelect={onChangeTab}
        selectedTab={selectedTab}
      />
      <div className="tab-content w-full">
        {loading ? 
          <SkeletonLoader type="list" count={3} listClassName='skel-loader h-[100px] px-5 py-8'/>
          : renderContent()
        }
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
    </>
  );
};

export default Events;
